<!--
 * @Description: 营销管理/活动运营报表/售卖卡券活动报表
-->
<template>
  <scroll-layout>
    <template #header>
      <ykc-search
        ref="YkcSearch"
        :data="searchItems"
        :searchForm="searchParams"
        @click="handleSearchConfirm"
        @clean="handleSearchClean" />
    </template>
    <div>
      <!-- 汇总数据区 -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--blue">
            <div class="summary-card__icon">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">全量活动实际参与用户总数</p>
              <p class="summary-card__value">{{ totalUsers || 0 | numberFormat }}</p>
            </div>
          </div>
          <div class="summary-card__tip">
            <i class="el-icon-info"></i>
            数据统计时间范围：{{ queryTimeRange }}
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--red">
            <div class="summary-card__icon">
              <i class="el-icon-s-flag"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">实际售出卡券电站数</p>
              <p class="summary-card__value">{{ totalSoldStationCount || 0 | numberFormat }}</p>
            </div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--green">
            <div class="summary-card__icon">
              <i class="el-icon-coin"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">实际发放卡券总数</p>
              <p class="summary-card__value">{{ totalSoldCount || 0 | numberFormat }}</p>
            </div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--amber">
            <div class="summary-card__icon">
              <i class="el-icon-circle-check"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">实际核销卡券总数</p>
              <p class="summary-card__value">{{ totalVerifyCount || 0 | numberFormat }}</p>
            </div>
          </div>
        </div>
      </div>

      <ykc-table
        ref="YkcTable"
        :data="tableData"
        :title="tableTitle"
        operateFixedType="right"
        :columns="tableColumns"
        :operateButtons="tableOperateButtons.filter(item => item.enabled())">
        <template #headerButtons>
          <div class="table-header-button">
            <ykc-button
              type="plain"
              v-rbac="'marketing:activityReport:saleCoupon:export'"
              @click="exportBlob">
              导出
            </ykc-button>
          </div>
        </template>
        <ykc-pagination
          slot="pagination"
          ref="YkcPagination"
          :total="total"
          :current-page.sync="currentPage"
          @current-change="handlePaginationCurrentChange"
          :page-size.sync="pageSize"
          @size-change="handlePaginationSizeChange" />
      </ykc-table>
    </div>
  </scroll-layout>
</template>

<script>
  import { offlineExport, getLastTime } from '@/utils';
  import { saleCouponReport } from '@/service/apis';

  export default {
    name: 'saleCouponReportList',
    data() {
      const startDate = getLastTime('yyyy-MM-dd', 30);
      const endDate = getLastTime('yyyy-MM-dd', 0);

      return {
        totalUsers: 0,
        totalSoldStationCount: 0,
        totalSoldCount: 0,
        totalVerifyCount: 0,
        queryTimeRange: '',
        searchParams: {
          queryTime: [startDate, endDate],
          queryTimeStart: startDate,
          queryTimeEnd: endDate,
        },
        tableData: [],
        total: 0,
        currentPage: 1,
        pageSize: 10,
        termList: [
          {
            title: '活动编码',
            desc: '活动的唯一标识编码，系统自动生成。',
          },
          {
            title: '实际参与用户数',
            desc: '实际购买卡券的用户数量，同一用户多次购买计为1人。',
          },
          {
            title: '实际售出卡券电站数',
            desc: '有卡券售出的电站数量统计。',
          },
          {
            title: '实际售卖卡券数量',
            desc: '成功售出的卡券总数量，包含用户购买的所有卡券。',
          },
          {
            title: '实际核销卡券数量',
            desc: '已使用核销的卡券数量，用户充电时使用卡券抵扣后计入核销。',
          },
          {
            title: '活动期间平均充电次数/电量',
            desc: '活动期间参与用户平均每人充电次数和充电电量。',
          },
          {
            title: '活动前30天平均充电次数/电量',
            desc: '活动开始前30天内，参与用户平均每人充电次数和充电电量，用于对比活动效果。',
          },
        ],
        tableTitle: '活动数据列表',
        tableOperateButtons: [],
        termMap: {
          actualParticipantCount: '特定活动，特定时间段（查询时间段）内购买了活动卡券的用户数',
          actualSoldStationCount:
            '特定活动，特定时间段（查询时间段）内有卡券售出的电站数（注：使用参与用户关联对应电站）',
          actualSoldCount: '特定活动，特定时间段（查询时间段）内发放卡券总数量',
          actualVerifyCount:
            '特定活动，特定时间段（查询时间段）内发放的卡券截止当前时间已被使用的数量',
          avgChargeTimes:
            '特定活动，特定时间段（查询时间段）内购买卡券用户活动期间内平均每天充电次数',
          avgChargePower:
            '特定活动，特定时间段（查询时间段）内购买卡券用户活动期间内平均每天充电度数',
          preAvgChargeTimes:
            '特定活动，特定时间段（查询时间段）内购买卡券用户在活动开始前30天平均每天充电次数',
          preAvgChargePower:
            '特定活动，特定时间段（查询时间段）内购买卡券用户在活动开始前30天平均每天充电度数',
        },
        tableColumns: [
          {
            label: '活动编码',
            prop: 'activityCode',
            minWidth: '180px',
          },
          {
            label: '活动名称',
            prop: 'activityName',
            minWidth: '200px',
          },
          {
            label: '活动类型',
            prop: 'activityType',
            minWidth: '100px',
            scopedSlots: {
              default: () => '售卖活动',
            },
          },
          {
            label: '活动开始时间',
            prop: 'activityBeginTime',
            minWidth: '160px',
          },
          {
            label: '活动结束时间',
            prop: 'activityEndTime',
            minWidth: '160px',
          },
          {
            label: '实际参与用户数',
            prop: 'actualParticipantCount',
            minWidth: '140px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) =>
                this.renderHeaderWithTip(column.label, 'actualParticipantCount'),
              default: ({ row }) => {
                return row.actualParticipantCount != null
                  ? Number(row.actualParticipantCount).toLocaleString()
                  : '——';
              },
            },
          },
          {
            label: '实际售出卡券电站数',
            prop: 'actualSoldStationCount',
            minWidth: '160px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) =>
                this.renderHeaderWithTip(column.label, 'actualSoldStationCount'),
              default: ({ row }) => {
                return row.actualSoldStationCount != null
                  ? Number(row.actualSoldStationCount).toLocaleString()
                  : '——';
              },
            },
          },
          {
            label: '实际售卖卡券数量',
            prop: 'actualSoldCount',
            minWidth: '160px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'actualSoldCount'),
              default: ({ row }) => {
                return row.actualSoldCount != null
                  ? Number(row.actualSoldCount).toLocaleString()
                  : '——';
              },
            },
          },
          {
            label: '实际核销卡券数量',
            prop: 'actualVerifyCount',
            minWidth: '160px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'actualVerifyCount'),
              default: ({ row }) => {
                return row.actualVerifyCount != null
                  ? Number(row.actualVerifyCount).toLocaleString()
                  : '——';
              },
            },
          },
          {
            label: '活动期间平均充电次数',
            prop: 'avgChargeTimes',
            minWidth: '180px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'avgChargeTimes'),
              default: ({ row }) => {
                return row.avgChargeTimes != null ? row.avgChargeTimes : '——';
              },
            },
          },
          {
            label: '活动期间平均充电电量',
            prop: 'avgChargePower',
            minWidth: '180px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'avgChargePower'),
              default: ({ row }) => {
                return row.avgChargePower != null ? row.avgChargePower : '——';
              },
            },
          },
          {
            label: '活动前30天平均充电次数',
            prop: 'preAvgChargeTimes',
            minWidth: '200px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'preAvgChargeTimes'),
              default: ({ row }) => {
                return row.preAvgChargeTimes != null ? row.preAvgChargeTimes : '——';
              },
            },
          },
          {
            label: '活动前30天平均充电电量',
            prop: 'preAvgChargePower',
            minWidth: '200px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'preAvgChargePower'),
              default: ({ row }) => {
                return row.preAvgChargePower != null ? row.preAvgChargePower : '——';
              },
            },
          },
        ],
      };
    },
    computed: {
      searchItems() {
        return [
          {
            comName: 'YkcInput',
            key: 'activityCode',
            label: '活动编码',
            placeholder: '请输入活动编码',
          },
          {
            comName: 'YkcInput',
            key: 'activityName',
            label: '活动名称',
            placeholder: '请输入活动名称',
          },
          {
            comName: 'YkcDatePicker',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            clearable: true,
            key: 'activityBeginTime',
            label: '活动开始时间',
            limitDate: 'limitAfter',
          },
          {
            comName: 'YkcDatePicker',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            key: 'queryTime',
            label: '数据查询时间',
            limitDate: 'limitAfter',
          },
        ];
      },
    },
    created() {
      this.getData();
    },
    methods: {
      /**
       * 渲染带术语提示的表头
       */
      renderHeaderWithTip(label, prop) {
        const tip = this.termMap[prop];
        if (!tip) return label;
        return (
          <span style="display: inline-flex; align-items: center; gap: 4px;">
            {label}
            <el-tooltip content={tip} placement="top">
              <i
                class="el-icon-question"
                style="color: #909399; cursor: pointer; font-size: 14px;"
              />
            </el-tooltip>
          </span>
        );
      },
      /**
       * 点击查询
       */
      handleSearchConfirm(form) {
        this.currentPage = 1;
        const search = JSON.parse(JSON.stringify(form));
        if (search.activityBeginTime && search.activityBeginTime.length === 2) {
          [search.activityBeginTimeStart, search.activityBeginTimeEnd] = search.activityBeginTime;
        }
        if (search.queryTime && search.queryTime.length === 2) {
          [search.queryTimeStart, search.queryTimeEnd] = search.queryTime;
        }
        this.searchParams = search || {};
        this.getData();
      },
      /**
       * 点击清空查询按钮
       */
      handleSearchClean(form) {
        Object.assign(this.searchParams, form);
        const startDate = getLastTime('yyyy-MM-dd', 30);
        const endDate = getLastTime('yyyy-MM-dd', 0);
        this.searchParams.queryTime = [startDate, endDate];
        this.searchParams.queryTimeStart = startDate;
        this.searchParams.queryTimeEnd = endDate;
      },
      /**
       * 导出
       */
      exportBlob() {
        offlineExport({
          downloadType: 'marketing_export',
          jsonNode: {
            downloadKey: 'activity_report_saleCoupon',
            ...this.searchParams,
          },
        });
      },

      /**
       * 分页 change 事件监听
       */
      handlePaginationCurrentChange(current) {
        this.currentPage = current;
        this.getData();
      },
      /**
       * 分页 size change 事件监听
       */
      handlePaginationSizeChange(size) {
        this.currentPage = 1;
        this.pageSize = size;
        this.getData();
      },
      /**
       * 获取远程数据
       */
      getData() {
        const loading = this.$loading({
          lock: true,
          text: 'Loading',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)',
        });
        const listReq = saleCouponReport.list({
          ...(this.searchParams || {}),
          current: this.currentPage,
          size: this.pageSize,
        });
        const summaryReq = saleCouponReport.summary({
          ...(this.searchParams || {}),
        });
        Promise.all([listReq, summaryReq])
          .then(([listRes, summaryRes]) => {
            this.tableData = listRes.records || [];
            this.total = listRes.total || 0;
            this.totalUsers = summaryRes.totalParticipants || 0;
            this.totalSoldStationCount = summaryRes.totalSoldStationCount || 0;
            this.totalSoldCount = summaryRes.totalSoldCount || 0;
            this.totalVerifyCount = summaryRes.totalVerifyCount || 0;
            this.queryTimeRange = summaryRes.queryTimeRange || '';
          })
          .catch(err => {
            console.error('saleCouponReport getData error:', err);
          })
          .finally(() => {
            loading.close();
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/components/ykc-ui/styles/var.scss';

  .summary-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
  }

  .summary-card {
    flex: 1;
    background: #fff;
    border: 1px solid #ebebf0;
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &__inner {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 20px;
      border-radius: 8px;
      border: 1px solid;

      &--blue {
        background: linear-gradient(to right, #eff6ff, #fff);
        border-color: #bfdbfe;
      }

      &--green {
        background: linear-gradient(to right, #ecfdf5, #fff);
        border-color: #a7f3d0;
      }

      &--amber {
        background: linear-gradient(to right, #fffbeb, #fff);
        border-color: #fde68a;
      }

      &--red {
        background: linear-gradient(to right, #fef2f2, #fff);
        border-color: #fecaca;
      }
    }

    &__icon {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      .summary-card__inner--blue & {
        background: rgba(37, 99, 235, 0.1);

        i {
          font-size: 24px;
          color: #2563eb;
        }
      }

      .summary-card__inner--green & {
        background: rgba(16, 185, 129, 0.1);

        i {
          font-size: 24px;
          color: #10b981;
        }
      }

      .summary-card__inner--amber & {
        background: rgba(245, 158, 11, 0.1);

        i {
          font-size: 24px;
          color: #f59e0b;
        }
      }

      .summary-card__inner--red & {
        background: rgba(239, 68, 68, 0.1);

        i {
          font-size: 24px;
          color: #ef4444;
        }
      }
    }

    &__info {
      display: flex;
      flex-direction: column;
    }

    &__label {
      font-size: 12px;
      color: #64748b;
      margin: 0 0 4px 0;
    }

    &__value {
      font-size: 24px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }

    &__tip {
      font-size: 12px;
      color: #64748b;
      margin-top: 8px;

      i {
        margin-right: 4px;
      }
    }
  }

  .term-list {
    .term-item {
      background: #f6f7f8;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      &__title {
        font-size: 14px;
        font-weight: 500;
        color: #1e293b;
        margin: 0 0 4px 0;
      }

      &__desc {
        font-size: 13px;
        color: #64748b;
        margin: 0;
      }
    }
  }
</style>
